<script setup>
import { ref, onMounted, watch } from 'vue'
import axios from 'axios'
import Header from './component/header.vue'

const loading   = ref(false)
const saving    = ref(false)
const bills     = ref({ data: [], current_page: 1, last_page: 1, total: 0 })
const summary   = ref({ total_open: 0, total_partially_paid: 0, amount_due: 0 })
const statusTab = ref('')
const search    = ref('')
const page      = ref(1)
const showModal = ref(false)
const showPayModal = ref(false)
const selectedBill = ref(null)
const formError = ref('')
const paying    = ref(false)

const vendors  = ref([])
const accounts = ref([])

const blankItem = () => ({ description: '', quantity: 1, unit_price: 0, amount: 0, vat_rate: 0 })
const blankForm = () => ({
  vendor_id: '',
  invoice_number: '',
  invoice_date: new Date().toISOString().slice(0, 10),
  due_date: '',
  description: '',
  expense_account_id: '',
  items: [blankItem()],
})
const form = ref(blankForm())
const payForm = ref({ amount: '', payment_date: new Date().toISOString().slice(0, 10) })

const load = async () => {
  try {
    loading.value = true
    const params = { per_page: 20, page: page.value }
    if (statusTab.value) params.status = statusTab.value
    if (search.value)    params.search = search.value
    const res = await axios.get('/accounting/bills', { params })
    bills.value   = res.data.data.bills
    summary.value = res.data.data.summary
  } catch (e) { console.error(e) } finally { loading.value = false }
}

const loadMeta = async () => {
  try {
    const [v, a] = await Promise.all([
      axios.get('/procurement/vendors', { params: { per_page: 200 } }),
      axios.get('/accounting/active-ledgers'),
    ])
    vendors.value  = v.data.data?.vendors?.data ?? v.data.data?.data ?? []
    const raw = a.data.data ?? {}
    accounts.value = (Array.isArray(raw) ? raw : Object.values(raw).flat()).filter(Boolean)
  } catch (e) {
    console.error('loadMeta failed', e)
  }
}

watch(statusTab, () => { page.value = 1; load() })
watch(page, load)
let st = null
const onSearch = () => { clearTimeout(st); st = setTimeout(() => { page.value = 1; load() }, 350) }
onMounted(() => { load(); loadMeta() })

const calcItem = (item) => {
  item.amount = parseFloat(item.quantity || 1) * parseFloat(item.unit_price || 0)
}
const addItem  = () => form.value.items.push(blankItem())
const removeItem = (i) => { if (form.value.items.length > 1) form.value.items.splice(i, 1) }
const subtotal  = () => form.value.items.reduce((s, i) => s + parseFloat(i.amount || 0), 0)
const vatTotal  = () => form.value.items.reduce((s, i) => s + (parseFloat(i.amount || 0) * parseFloat(i.vat_rate || 0) / 100), 0)
const grandTotal = () => subtotal() + vatTotal()

const openCreate = () => {
  form.value = blankForm()
  formError.value = ''
  showModal.value = true
}

const saveBill = async () => {
  if (!form.value.invoice_date || !form.value.items.length) {
    formError.value = 'Invoice date and at least one item are required.'
    return
  }
  try {
    saving.value = true
    formError.value = ''
    await axios.post('/accounting/bills', form.value)
    showModal.value = false
    load()
  } catch (e) {
    formError.value = e.response?.data?.message ?? 'Failed to save bill'
  } finally { saving.value = false }
}

const openPayModal = (bill) => {
  selectedBill.value = bill
  payForm.value = { amount: (bill.total_amount - bill.amount_paid).toFixed(2), payment_date: new Date().toISOString().slice(0, 10) }
  showPayModal.value = true
}

const submitPayment = async () => {
  try {
    paying.value = true
    await axios.post(`/accounting/bills/${selectedBill.value.id}/payment`, payForm.value)
    showPayModal.value = false
    load()
  } catch (e) { alert(e.response?.data?.message ?? 'Payment failed') } finally { paying.value = false }
}

const deleteBill = async (id) => {
  if (!confirm('Delete this bill?')) return
  await axios.delete(`/accounting/bills/${id}`)
  load()
}

const statusLabel = (s) => ({ draft: 'Draft', open: 'Open', partially_paid: 'Partial', paid: 'Paid', cancelled: 'Cancelled' }[s] ?? s)
const statusClass = (s) => ({ draft: 'badge--grey', open: 'badge--blue', partially_paid: 'badge--orange', paid: 'badge--green', cancelled: 'badge--red' }[s] ?? '')
const fmt = (v) => Number(v ?? 0).toLocaleString('en-NG', { minimumFractionDigits: 2 })
</script>

<template>
  <div>
    <Header />
    <div class="bills-page">

      <div class="bill-chips">
        <div class="bill-chip bill-chip--blue">
          <span class="chip-label">Open</span>
          <span class="chip-val">{{ summary.total_open }}</span>
        </div>
        <div class="bill-chip bill-chip--orange">
          <span class="chip-label">Partially Paid</span>
          <span class="chip-val">{{ summary.total_partially_paid }}</span>
        </div>
        <div class="bill-chip bill-chip--red">
          <span class="chip-label">Amount Due</span>
          <span class="chip-val">₦{{ fmt(summary.amount_due) }}</span>
        </div>
      </div>

      <div class="bill-toolbar">
        <div class="bill-tabs">
          <button :class="['bill-tab', statusTab === '' ? 'bill-tab--active' : '']" @click="statusTab = ''">All</button>
          <button :class="['bill-tab', statusTab === 'open' ? 'bill-tab--active' : '']" @click="statusTab = 'open'">Open</button>
          <button :class="['bill-tab', statusTab === 'partially_paid' ? 'bill-tab--active' : '']" @click="statusTab = 'partially_paid'">Partial</button>
          <button :class="['bill-tab', statusTab === 'paid' ? 'bill-tab--active' : '']" @click="statusTab = 'paid'">Paid</button>
        </div>
        <div class="bill-toolbar-right">
          <input v-model="search" class="bill-search" placeholder="Search bills…" @input="onSearch" />
          <button class="btn-primary" @click="openCreate">+ New Bill</button>
        </div>
      </div>

      <div class="table-card">
        <div class="table-scroll">
          <table class="data-table">
            <thead>
              <tr>
                <th>Bill #</th>
                <th>Vendor</th>
                <th>Invoice #</th>
                <th>Invoice Date</th>
                <th>Due Date</th>
                <th>Total</th>
                <th>Paid</th>
                <th>Balance</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="loading"><td colspan="10" class="tbl-loading">Loading…</td></tr>
              <tr v-else-if="!bills.data.length"><td colspan="10" class="tbl-empty">No bills found</td></tr>
              <tr v-for="b in bills.data" :key="b.id" class="data-row">
                <td><span class="pv-ref">{{ b.bill_number }}</span></td>
                <td>{{ b.vendor?.name ?? '—' }}</td>
                <td>{{ b.invoice_number ?? '—' }}</td>
                <td>{{ b.invoice_date }}</td>
                <td>{{ b.due_date ?? '—' }}</td>
                <td class="amt">{{ fmt(b.total_amount) }}</td>
                <td class="amt">{{ fmt(b.amount_paid) }}</td>
                <td class="amt bold">{{ fmt(b.total_amount - b.amount_paid) }}</td>
                <td><span :class="['badge', statusClass(b.status)]">{{ statusLabel(b.status) }}</span></td>
                <td>
                  <button v-if="b.status !== 'paid' && b.status !== 'cancelled'" class="btn-link" @click="openPayModal(b)">Pay</button>
                  <button v-if="b.status !== 'paid'" class="btn-link btn-link--red" @click="deleteBill(b.id)">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-if="bills.last_page > 1" class="pv-pagination">
          <button :disabled="page <= 1" @click="page--">‹</button>
          <span>{{ page }} / {{ bills.last_page }}</span>
          <button :disabled="page >= bills.last_page" @click="page++">›</button>
        </div>
      </div>
    </div>

    <!-- Create Bill Modal -->
    <div v-if="showModal" class="modal-overlay" @click.self="showModal = false">
      <div class="modal-box modal-box--lg">
        <div class="modal-header">
          <h3>New Bill</h3>
          <button class="modal-close" @click="showModal = false">×</button>
        </div>
        <div class="modal-body">
          <div v-if="formError" class="form-error">{{ formError }}</div>
          <div class="form-grid">
            <div class="form-group">
              <label>Vendor</label>
              <select v-model="form.vendor_id">
                <option value="">— Select Vendor —</option>
                <option v-for="vn in vendors" :key="vn.id" :value="vn.id">{{ vn.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>Invoice Number</label>
              <input v-model="form.invoice_number" />
            </div>
            <div class="form-group">
              <label>Invoice Date *</label>
              <input type="date" v-model="form.invoice_date" />
            </div>
            <div class="form-group">
              <label>Due Date</label>
              <input type="date" v-model="form.due_date" />
            </div>
            <div class="form-group">
              <label>Expense Account</label>
              <select v-model="form.expense_account_id">
                <option value="">— Default COGS —</option>
                <option v-for="a in accounts.filter(x => x.type === 'expense')" :key="a.id" :value="a.id">{{ a.code }} – {{ a.name }}</option>
              </select>
            </div>
            <div class="form-group">
              <label>Description</label>
              <input v-model="form.description" />
            </div>
          </div>

          <!-- Line items -->
          <div class="items-section">
            <div class="items-header">
              <h4>Line Items</h4>
              <button class="btn-link" @click="addItem">+ Add Item</button>
            </div>
            <table class="items-table">
              <thead><tr><th>Description</th><th>Qty</th><th>Unit Price</th><th>VAT %</th><th>Amount</th><th></th></tr></thead>
              <tbody>
                <tr v-for="(item, i) in form.items" :key="i">
                  <td><input v-model="item.description" placeholder="Item description" class="item-input" /></td>
                  <td><input v-model.number="item.quantity" type="number" min="0" step="0.001" class="item-num" @input="calcItem(item)" /></td>
                  <td><input v-model.number="item.unit_price" type="number" min="0" step="0.01" class="item-num" @input="calcItem(item)" /></td>
                  <td><input v-model.number="item.vat_rate" type="number" min="0" max="100" step="0.01" class="item-num" /></td>
                  <td class="amt bold">{{ fmt(item.amount) }}</td>
                  <td><button v-if="form.items.length > 1" class="btn-link btn-link--red" @click="removeItem(i)">✕</button></td>
                </tr>
              </tbody>
            </table>
            <div class="items-totals">
              <span>Subtotal: ₦{{ fmt(subtotal()) }}</span>
              <span>VAT: ₦{{ fmt(vatTotal()) }}</span>
              <span class="bold">Total: ₦{{ fmt(grandTotal()) }}</span>
            </div>
          </div>

          <div class="modal-actions">
            <button class="btn-outline" @click="showModal = false">Cancel</button>
            <button class="btn-primary" :disabled="saving" @click="saveBill">{{ saving ? 'Saving…' : 'Create Bill' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Pay Modal -->
    <div v-if="showPayModal" class="modal-overlay" @click.self="showPayModal = false">
      <div class="modal-box">
        <div class="modal-header">
          <h3>Record Payment – {{ selectedBill?.bill_number }}</h3>
          <button class="modal-close" @click="showPayModal = false">×</button>
        </div>
        <div class="modal-body">
          <div class="form-grid">
            <div class="form-group">
              <label>Amount *</label>
              <input type="number" v-model="payForm.amount" min="0" step="0.01" />
            </div>
            <div class="form-group">
              <label>Payment Date *</label>
              <input type="date" v-model="payForm.payment_date" />
            </div>
          </div>
          <div class="modal-actions">
            <button class="btn-outline" @click="showPayModal = false">Cancel</button>
            <button class="btn-primary" :disabled="paying" @click="submitPayment">{{ paying ? 'Processing…' : 'Record Payment' }}</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bills-page { padding: 0 24px 40px; }
.bill-chips { display: flex; gap: 12px; margin-bottom: 20px; flex-wrap: wrap; }
.bill-chip { display: flex; flex-direction: column; align-items: center; padding: 12px 20px; border-radius: 8px; min-width: 120px; }
.bill-chip--blue   { background: #eef4ff; }
.bill-chip--orange { background: #fff8ee; }
.bill-chip--red    { background: #fff0f0; }
.chip-label { font-size: 12px; color: #888; margin-bottom: 4px; }
.chip-val   { font-size: 20px; font-weight: 700; color: #1a2a45; }
.bill-toolbar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; gap: 12px; flex-wrap: wrap; }
.bill-tabs { display: flex; gap: 4px; }
.bill-tab  { padding: 7px 14px; border: 1px solid #dde3ef; border-radius: 6px; background: #fff; font-size: 13px; cursor: pointer; color: #555; }
.bill-tab--active { background: #004AAD; color: #fff; border-color: #004AAD; }
.bill-toolbar-right { display: flex; gap: 10px; align-items: center; }
.bill-search { padding: 8px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13px; width: 220px; }
.table-card  { background: #fff; border-radius: 10px; border: 1px solid #eaf0fb; overflow: hidden; }
.table-scroll { overflow-x: auto; }
.data-table  { width: 100%; border-collapse: collapse; font-size: 13.5px; }
.data-table th { padding: 12px 14px; text-align: left; background: #f7f9fc; font-weight: 600; color: #556; font-size: 12px; white-space: nowrap; }
.data-table td { padding: 12px 14px; border-top: 1px solid #f0f3fa; vertical-align: middle; }
.data-row:hover td { background: #fafbff; }
.tbl-loading, .tbl-empty { text-align: center; color: #999; padding: 40px; }
.amt { text-align: right; font-variant-numeric: tabular-nums; }
.bold { font-weight: 600; }
.pv-ref { font-weight: 600; color: #004AAD; }
.pv-pagination { display: flex; align-items: center; gap: 12px; justify-content: center; padding: 12px; }
.pv-pagination button { padding: 5px 12px; border: 1px solid #dde3ef; border-radius: 4px; cursor: pointer; background: #fff; }
.pv-pagination button:disabled { opacity: 0.4; cursor: not-allowed; }
.badge         { display: inline-block; padding: 3px 10px; border-radius: 999px; font-size: 11.5px; font-weight: 600; }
.badge--grey   { background: #f0f0f0; color: #666; }
.badge--blue   { background: #cfe2ff; color: #084298; }
.badge--orange { background: #fff3cd; color: #856404; }
.badge--green  { background: #d1e7dd; color: #0f5132; }
.badge--red    { background: #f8d7da; color: #842029; }
.btn-primary  { padding: 9px 20px; background: #004AAD; color: #fff; border: none; border-radius: 6px; font-size: 13.5px; font-weight: 600; cursor: pointer; }
.btn-outline  { padding: 9px 20px; background: #fff; color: #004AAD; border: 1px solid #004AAD; border-radius: 6px; font-size: 13.5px; cursor: pointer; }
.btn-link     { background: none; border: none; color: #004AAD; font-size: 13px; cursor: pointer; padding: 0 4px; }
.btn-link--red { color: #dc3545; }
.modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1000; display: flex; align-items: flex-start; justify-content: center; padding-top: 60px; }
.modal-box     { background: #fff; border-radius: 12px; width: 600px; max-width: 95vw; max-height: 85vh; overflow-y: auto; box-shadow: 0 8px 40px rgba(0,0,0,0.18); }
.modal-box--lg { width: 800px; }
.modal-header  { display: flex; align-items: center; justify-content: space-between; padding: 20px 24px 16px; border-bottom: 1px solid #eaf0fb; }
.modal-header h3 { font-size: 16px; font-weight: 700; color: #1a2a45; margin: 0; }
.modal-close   { background: none; border: none; font-size: 22px; color: #888; cursor: pointer; }
.modal-body    { padding: 20px 24px 24px; }
.modal-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.form-grid     { display: grid; grid-template-columns: 1fr 1fr; gap: 14px 18px; margin-bottom: 16px; }
.form-group    { display: flex; flex-direction: column; gap: 5px; }
.form-group label { font-size: 12.5px; font-weight: 600; color: #556; }
.form-group input, .form-group select { padding: 9px 12px; border: 1px solid #dde3ef; border-radius: 6px; font-size: 13.5px; color: #1a2a45; }
.form-error { background: #f8d7da; color: #842029; padding: 10px 14px; border-radius: 6px; font-size: 13px; margin-bottom: 14px; }
.items-section { border-top: 1px solid #eaf0fb; padding-top: 16px; }
.items-header  { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.items-header h4 { font-size: 13.5px; font-weight: 700; color: #1a2a45; margin: 0; }
.items-table { width: 100%; border-collapse: collapse; font-size: 13px; margin-bottom: 10px; }
.items-table th { padding: 8px 10px; background: #f7f9fc; font-size: 12px; font-weight: 600; color: #556; text-align: left; }
.items-table td { padding: 7px 10px; border-top: 1px solid #f0f3fa; vertical-align: middle; }
.item-input { width: 100%; padding: 6px 8px; border: 1px solid #dde3ef; border-radius: 4px; font-size: 13px; }
.item-num   { width: 80px; padding: 6px 8px; border: 1px solid #dde3ef; border-radius: 4px; font-size: 13px; text-align: right; }
.items-totals { display: flex; gap: 20px; justify-content: flex-end; font-size: 13px; color: #556; margin-top: 8px; }
</style>
